/**
 * CRMContext.tsx
 * 
 * Backward-compatible context wrapper over standalone React Query hooks.
 * All data fetching and mutations are delegated to hooks/useCRMQueries.ts.
 * 
 * Existing consumers can continue using `useCRM()` without changes.
 * New code should prefer importing individual hooks directly from useCRMQueries.ts.
 */
import React, { createContext, useContext, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Lead, Client, Appointment, LeadStatus, DesignProject } from '../types';
import { useIdioma } from '../i18n/idioma';
import {
    findDuplicateClient, findDuplicateContact, describeAddressAlsoUsed, clientContactRecord,
} from '../services/clientValidation';
import { findStageBlock, describeStageBlock, stageLockedError } from '../utils/leadStageFlow';
import {
    attachLeadWorkToClient, claimLeadConversion, releaseLeadConversion, fetchClientsFresh,
} from '../services/database';
import { useWorkspace } from './WorkspaceContext';
import {
    // Queries
    useLeads, useClients, useAppointments,
    // Mutations
    useAddLead, useUpdateLead, useDeleteLeads, useAddClient, useUpdateClient, useDeleteClient,
    useAddAppointment, useUpdateAppointment, useDeleteAppointment,
} from '../hooks/useCRMQueries';

// --- DESIGN PORTFOLIO (dormant) ---
// There is no table, query, or write path behind this — it has always been an
// empty array. Because of that the whole feature could never do anything: the
// lead panel's design picker had no options, its "Present" button was
// permanently disabled, and /design-showcase could only render "No Design
// Available".
//
// The UI that read this was removed (lead panel picker, /design-showcase
// route); the view file and the DesignProject type are kept. Do NOT surface
// this again until it has a real data source — a portfolio table, CRUD, and
// before/after image upload.
const MOCK_DESIGN_PROJECTS: DesignProject[] = [];

interface CRMContextType {
    leads: Lead[];
    clients: Client[];
    appointments: Appointment[];
    addLead: (lead: Omit<Lead, 'id' | 'dateCreated' | 'status' | 'tags'>) => Promise<void>;
    updateLeadStatus: (id: string, status: LeadStatus) => void;
    updateLead: (id: string, updates: Partial<Lead>) => Promise<void>;
    updateLeadDesign: (leadId: string, designId: string) => Promise<void>;
    /** Quita prospectos del tablero para siempre. Hoy solo admite ganados —su
     *  ficha de cliente conserva todo—; rechaza la lista entera si alguno no lo
     *  es. Lo usan la limpieza de la columna Ganado y el panel de detalle. */
    deleteLeads: (ids: string[]) => Promise<void>;
    /** Creates the client, then marks the lead Won. Rejects on duplicate, on a
     *  stage that no longer admits Ganado, or on a write failure — callers must
     *  surface the message.
     *  `porFirma` salta la regla de etapa: la firma del cliente en el enlace
     *  público cierra el trato aunque el prospecto estuviera marcado Perdido.
     *  `direccion` es la que se acaba de escribir en el cuadro de la conversión;
     *  ver por qué hace falta pasarla en la propia función. */
    convertLeadToClient: (leadId: string, opciones?: { porFirma?: boolean; direccion?: string }) => Promise<string>;
    addClient: (client: Omit<Client, 'id' | 'dateCreated' | 'totalSpent'>) => Promise<string>;
    updateClient: (id: string, updates: Partial<Client>) => Promise<void>;
    deleteClient: (id: string) => Promise<void>;
    addAppointment: (appt: Omit<Appointment, 'id'>) => Promise<void>;
    updateAppointment: (id: string, updates: Partial<Appointment>) => Promise<void>;
    deleteAppointment: (id: string) => Promise<void>;
    designProjects: DesignProject[];
    // Distinct load/error signals for the Clients view so an unresolved or
    // errored workspace fetch never renders as an empty client list.
    clientsLoading: boolean;
    clientsError: boolean;
    retryClients: () => void;
    /* Mismas señales para prospectos. Faltaban, y por eso el tablero de Leads
       pintaba siete columnas con "Nothing here" mientras aún estaba cargando —
       indistinguible de no tener ni un prospecto. */
    leadsLoading: boolean;
    leadsError: boolean;
    retryLeads: () => void;
}

const CRMContext = createContext<CRMContextType | undefined>(undefined);

export const useCRM = () => {
    const context = useContext(CRMContext);
    if (!context) {
        throw new Error('useCRM must be used within a CRMProvider');
    }
    return context;
};

export const CRMProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // `activeWorkspace` se leía aquí solo para el barrido de ganados que ya no
    // existe; las consultas y mutaciones resuelven su taller por su cuenta.
    const { status: workspaceStatus, retry: retryWorkspace } = useWorkspace();
    const { t } = useIdioma();
    const qc = useQueryClient();

    // --- QUERIES (delegated to standalone hooks) ---
    const leadsQuery = useLeads();
    const leads = leadsQuery.data ?? [];
    const clientsQuery = useClients();
    const clients = clientsQuery.data ?? [];
    const { data: appointments = [] } = useAppointments();

    // Distinct load/error signals for ClientsView. The clients query stays
    // DISABLED until the workspace resolves, so its own isLoading is false while
    // the workspace is still booting — fold the workspace status in so an
    // unresolved/errored fetch NEVER renders as an empty client list.
    const clientsError = workspaceStatus === 'error' || clientsQuery.isError;
    const clientsLoading = !clientsError && (workspaceStatus !== 'resolved' || clientsQuery.isLoading);

    // Idéntico para prospectos, y por la misma razón: la consulta está DESHABILITADA
    // hasta que el workspace resuelve, así que su isLoading es false mientras
    // arranca. Sin plegar el estado del workspace, ese hueco se pinta como
    // "no tienes prospectos".
    const leadsError = workspaceStatus === 'error' || leadsQuery.isError;
    const leadsLoading = !leadsError && (workspaceStatus !== 'resolved' || leadsQuery.isLoading);

    /* Aquí vivía `useWonLeadCleanup`, que borraba los prospectos ganados 24 h
       después de ganarlos. Ya no: un prospecto ganado se QUEDA, cerrado en su
       columna, porque es el registro de que ese trato se cerró y de cuándo. Se
       borraba para que el tablero no creciera, y a cambio la ficha desaparecía
       sola de la vista de quien la había ganado el día antes. */

    // --- MUTATIONS (delegated to standalone hooks) ---
    const addLeadMut = useAddLead();
    const updateLeadMut = useUpdateLead();
    const deleteLeadsMut = useDeleteLeads();
    const addClientMut = useAddClient();
    const updateClientMut = useUpdateClient();
    const deleteClientMut = useDeleteClient();
    const addAppointmentMut = useAddAppointment();
    const updateAppointmentMut = useUpdateAppointment();
    const deleteAppointmentMut = useDeleteAppointment();

    // --- Adapter functions to preserve the original API ---
    const addLead = async (leadData: Omit<Lead, 'id' | 'dateCreated' | 'status' | 'tags'>) => {
        await addLeadMut.mutateAsync(leadData);
    };

    const updateLead = async (id: string, updates: Partial<Lead>) => {
        await updateLeadMut.mutateAsync({ id, updates });
    };

    const deleteLeads = async (ids: string[]) => {
        await deleteLeadsMut.mutateAsync(ids);
    };

    /* Reaching Won ALWAYS creates the client record — whether it came from the
       Convert button, a drag onto the Won column, or the stage picker.
       El prospecto ganado se QUEDA en el tablero, en Ganado y sin poder moverse:
       es el registro de que ese trato se cerró. Antes se borraba a las 24 h y la
       ficha desaparecía del tablero sola. */
    const updateLeadStatus = (id: string, status: LeadStatus) => {
        if (status === LeadStatus.WON) {
            /* El fallo se ENSEÑA. Este es el camino del arrastre a la columna y
               del selector de etapa, y hasta ahora el error se tragaba entero:
               la tarjeta volvía a su sitio sin decir por qué. Con el bloqueo por
               nombre repetido eso pasa a ser corriente —dos "Jose" en la libreta
               son suficientes—, y un rechazo mudo se lee como que la app está
               rota. El botón Convertir ya avisaba por su cuenta (useLeadActions);
               esto es lo mismo para las otras dos puertas. */
            void convertLeadToClient(id).catch((err: any) => {
                toast.error(err?.message || t('prospectos.convertir.error'));
            });
            return;
        }
        updateLead(id, { status });
    };

    const updateLeadDesign = async (leadId: string, designId: string) => {
        await updateLead(leadId, { designProjectId: designId });
    };

    /**
     * Turns a lead into a permanent client and marks the deal Won.
     *
     * Ordering matters: the client is created FIRST and any failure propagates
     * to the caller. Marking the lead Won before knowing the client landed is
     * what used to strand leads in a "won, but no customer exists" state — the
     * previous version logged insert errors to the console and marked the lead
     * Won anyway.
     *
     * Routing through `addClientMut` instead of a raw insert is deliberate: it
     * is what subjects this path to the same duplicate phone/email/address
     * block as the New Client form. A raw insert bypassed it entirely, and
     * there is no UNIQUE index in the database to catch the fallout.
     *
     * Al final traspasa al cliente todo lo que se hizo mientras era prospecto:
     * las propuestas armadas para él (`estimates.source_lead_id`) y el proyecto
     * que alguna hubiera generado. Eso corre en TODOS los caminos hacia Ganado
     * —botón Convertir, arrastre a la columna, selector de etapa, y la propuesta
     * marcada como Pagada— porque una vez en Ganado la etapa no vuelve atrás:
     * lo que no se traspase aquí se queda colgado del prospecto para siempre,
     * y no hay pantalla que permita reasignarlo.
     *
     * @returns the new client's id.
     * @throws if the client is a duplicate, or either write fails.
     */
    const convertLeadToClient = async (leadId: string, opciones?: { porFirma?: boolean; direccion?: string }): Promise<string> => {
        const lead = leads.find(l => l.id === leadId);
        if (!lead) throw new Error('That lead no longer exists. Refresh the page and try again.');

        /* La dirección llega POR PARÁMETRO, y no es un capricho de la firma.
           `leads` es el array de ESTE render, y quien llama aquí acaba de
           guardar la dirección que le pidió al usuario en el cuadro de diálogo.
           Esa escritura no cambia el array que esta función tiene capturado —la
           llamada ya está en marcha con las referencias del render anterior—,
           así que `lead.address` sigue vacío justo cuando el usuario acaba de
           escribirlo. Resultado: la ficha de cliente nacía SIN dirección, con la
           dirección guardada solo en el prospecto, y en Clientes salía un guion.
           Por lo mismo, la comparación de duplicados de abajo tampoco veía la
           dirección recién escrita y no podía chocar con nada. */
        const address = opciones?.direccion?.trim() || lead.address || '';

        /* La puerta de PROCESO, antes de crear nada.
           Tiene que estar aquí arriba y no en la escritura final: esta función
           crea el cliente PRIMERO y marca Ganado al final, así que dejar pasar un
           prospecto perdido y frenarlo abajo dejaría una ficha de cliente creada
           por un movimiento que nunca se llegó a permitir.
           `porFirma` es la única excepción, y no es un atajo de la interfaz: el
           cliente firmó la propuesta desde el enlace público. Ahí el trato está
           cerrado de verdad, y bloquearlo dejaría una propuesta firmada sin ficha
           de cliente a la que engancharla. */
        if (!opciones?.porFirma) {
            const bloqueo = findStageBlock(lead.status, LeadStatus.WON);
            if (bloqueo) throw stageLockedError(describeStageBlock(t, bloqueo, lead.name));
        }

        // The client record has no home for service interest or deal value, so
        // fold them into the notes rather than dropping what the sales
        // conversation captured.
        const provenance = [
            'Converted from lead.',
            lead.serviceInterest ? `Interest: ${lead.serviceInterest}.` : '',
            lead.value ? `Estimated value: $${lead.value.toLocaleString()}.` : '',
        ].filter(Boolean).join(' ');
        const propertyNotes = [lead.notes?.trim(), provenance].filter(Boolean).join('\n\n');

        /* Idempotent: this now runs on every route into Won (button, drag, stage
           picker), and re-winning an already-won lead must not mint a second
           client. Matches the same rules the Clients form uses.
           El NOMBRE va incluido y no es decorativo: sin él, la dirección sola
           daba por bueno cualquier cliente que viviera en el mismo edificio, y
           la conversión enganchaba las propuestas del prospecto a la ficha del
           vecino sin decir nada. */
        const alreadyAClient = findDuplicateClient(clients, {
            name: lead.name,
            phone: lead.phone,
            email: lead.email,
            address,
        });

        /* Quién crea la ficha, cuando hay varias pestañas mirando.
           La conversión ya no la dispara solo una persona: al firmar el cliente
           desde el enlace público, el barrido salta solo en CADA pestaña que
           tenga la app abierta. Dos a la vez creaban dos fichas del mismo
           cliente, porque cada una consulta su propia caché y `clients` no tiene
           índice único. La reserva la resuelve Postgres: de dos UPDATE
           simultáneos a la misma fila, exactamente uno toca algo. */
        let clientId: string;
        if (alreadyAClient) {
            clientId = alreadyAClient.existing.id;
        } else if (await claimLeadConversion(leadId)) {
            // useAddClient already enforces the workspace (requireWorkspaceId) and
            // rejects duplicates, so no manual scoping is needed here.
            try {
                clientId = await addClientMut.mutateAsync({
                    // La ficha de cliente COPIA el teléfono y el correo del
                    // prospecto, así que sin esto el propio prospecto —y
                    // cualquier duplicado suyo que ya estuviera en la lista—
                    // bloquearía la conversión por duplicado. Es una promoción,
                    // no un contacto nuevo: la agenda de prospectos no se mira.
                    fromLeadId: leadId,
                    /* Y por lo mismo que arriba no se aplica la puerta de
                       proceso: cuando el cliente ya FIRMÓ desde el enlace
                       público, nadie está delante para reescribir un nombre que
                       choque con el de otro cliente, y bloquear dejaría la
                       propuesta firmada sin ficha a la que engancharse. Solo
                       se levanta el bloqueo por NOMBRE; teléfono, correo y
                       dirección siguen mandando a la ficha que ya existe. */
                    porFirma: !!opciones?.porFirma,
                    name: lead.name,
                    phone: lead.phone,
                    email: lead.email || '',
                    address,
                    propertyNotes,
                    gateCode: '',
                    preferredContact: 'Phone',
                    // 'New' is a real member of GENERAL_CLIENT_TAGS. The previous
                    // hardcoded 'Maintenance' is not in any industry's tag catalog, so
                    // every converted lead showed a stray custom tag.
                    tags: ['New'],
                });
            } catch (err) {
                // Se suelta la reserva o el prospecto queda dos minutos sin poder
                // reintentarse, por un fallo que probablemente sea de un momento.
                await releaseLeadConversion(leadId);
                throw err;
            }

            /* Ficha nueva en una dirección que ya está en Clientes: se avisa.
               Ya no bloquea (`permitirDireccionRepetida`), y una conversión que
               se lleva por delante una comprobación en silencio es justo lo que
               hay que evitar: quien acaba de ganar el trato es quien puede decir
               si es la misma casa o el apartamento de al lado, y es AHORA cuando
               está mirando.

               `opciones.direccion` significa que la dirección se acaba de
               escribir en el cuadro de conversión, y ese cuadro ya lo avisó
               mientras se tecleaba. Se calla aquí para no decirlo dos veces. Sin
               ella se llega por el arrastre a Ganado, por el selector de etapa o
               por un prospecto que ya traía dirección: ahí no hubo cuadro
               ninguno y este toast es el único aviso que existe. */
            if (!opciones?.direccion && address) {
                const tambien = findDuplicateContact(
                    clients.map(clientContactRecord), { address }, { clientId },
                );
                if (tambien) toast(describeAddressAlsoUsed(t, tambien), { duration: 8000, icon: '📍' });
            }
        } else {
            /* Otra pestaña va por delante. NO se crea nada: se espera a su ficha.
               La caché local no la tiene todavía, así que se lee de la base. */
            const frescos = await fetchClientsFresh();
            const suyo = findDuplicateClient(frescos, {
                name: lead.name, phone: lead.phone, email: lead.email, address,
            });
            if (!suyo) {
                // El mensaje del Error acaba en un toast, así que se traduce
                // aquí: escrito a mano en español salía en español con la app
                // en inglés.
                throw new Error(t('crm.convertir.otroDispositivo'));
            }
            clientId = suyo.existing.id;
        }

        /* El traspaso va ANTES de marcar Ganado, y su fallo SÍ se relanza.
           Marcar Ganado CIERRA la etapa: a partir de ahí el prospecto no se
           mueve, así que una propuesta que no se haya traspasado se queda
           apuntando a un prospecto ganado y a ningún cliente, y no hay ninguna
           pantalla en la app que permita reasignar un estimado. Antes esto se
           registraba y se seguía, y esa combinación perdía la propuesta.
           Relanzar es seguro porque el reintento es idempotente: el cliente ya
           existe, `findDuplicateClient` lo encuentra arriba y solo se repite el
           traspaso, que limpia `source_lead_id` y por tanto no se aplica dos
           veces. */
        if (clientId) {
            const traspasadas = await attachLeadWorkToClient(leadId, clientId, lead.name);
            if (traspasadas > 0) {
                qc.invalidateQueries({ queryKey: ['estimates'] });
                qc.invalidateQueries({ queryKey: ['projects'] });
            }
        }

        // Preserve an existing stamp so re-converting never restarts the clock
        // that useWonLeadCleanup reads. Cuando la reserva es de esta llamada,
        // `won_at` ya quedó sellado por ella y se respeta igual.
        await updateLeadMut.mutateAsync({
            id: leadId,
            updates: { status: LeadStatus.WON, wonAt: lead.wonAt || new Date().toISOString() },
        });

        return clientId || '';
    };

    const addClient = async (clientData: Omit<Client, 'id' | 'dateCreated' | 'totalSpent'>): Promise<string> => {
        const result = await addClientMut.mutateAsync(clientData);
        return result || '';
    };

    const updateClient = async (id: string, updates: Partial<Client>) => {
        await updateClientMut.mutateAsync({ id, updates });
    };

    const deleteClient = async (id: string) => {
        await deleteClientMut.mutateAsync(id);
    };

    const addAppointment = async (apptData: Omit<Appointment, 'id'>) => {
        await addAppointmentMut.mutateAsync(apptData);
    };

    const updateAppointment = async (id: string, updates: Partial<Appointment>) => {
        await updateAppointmentMut.mutateAsync({ id, updates });
    };

    const deleteAppointment = async (id: string) => {
        await deleteAppointmentMut.mutateAsync(id);
    };

    return (
        <CRMContext.Provider value={{
            leads, clients, appointments,
            addLead, updateLeadStatus, updateLead, updateLeadDesign, deleteLeads, convertLeadToClient,
            addClient, updateClient, deleteClient, addAppointment, updateAppointment, deleteAppointment,
            designProjects: MOCK_DESIGN_PROJECTS,
            clientsLoading, clientsError, retryClients: retryWorkspace,
            leadsLoading, leadsError, retryLeads: retryWorkspace
        }}>
            {children}
        </CRMContext.Provider>
    );
};
